/**
 * Filter section for the DeliveryTab.
 *
 * Grouped into labelled sections (Quick Ranges · Company & Supplier ·
 * Order · Status · Dates) so users can scan faster. Compact input sizing.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Calendar, Search, Filter } from 'lucide-react';
import SearchableSelect from '../../ui/SearchableSelect';
import { useView } from '../../../contexts/ViewContext';

export function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const QUICK_RANGES = [
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'lastQuarter', label: 'Last Quarter' },
  { key: 'last12Months', label: 'Last 12 Months (inc. current month)' },
] as const;

export function getQuickRangeDates(range: string): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'lastMonth':
      return { dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)), dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const yr = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const q = lq < 0 ? 3 : lq;
      return { dateFrom: formatLocalDate(new Date(yr, q * 3, 1)), dateTo: formatLocalDate(new Date(yr, (q + 1) * 3, 0)) };
    }
    case 'last12Months':
      return { dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth() + 1, 1)), dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

interface FilterOption {
  value: string;
  label: string;
  group?: number | null;
}

interface DeliveryFilterSectionProps {
  filters: {
    grnNumber: string;
    poNumber: string;
    supplier: string;
    partNumber: string;
    lineNumber: string;
    inspection_status: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    supplierGroup: string;
    commodityGroup: string;
    commodityType: string;
    appealStatus: string;
    appealFilter: string;
    poLineStatus: string;
    grnStatus: string;
    oddFrom: string;
    oddTo: string;
    strategic: string;
    supplierCode: string;
  };
  textInputs: {
    grnNumber: string;
    poNumber: string;
    supplier: string;
    partNumber: string;
    lineNumber: string;
  };
  siteOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  supplierOptions: FilterOption[];
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  onHideFilters?: () => void;
}

export function DeliveryFilterSection({
  filters,
  textInputs,
  siteOptions,
  supplierGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  supplierOptions,
  onFilterChange,
  onClearFilters,
  onSearch,
  onHideFilters,
}: DeliveryFilterSectionProps) {
  // CR-4577: Filter site options by view context
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  const viewScopedSites = useMemo(() => {
    if (viewLevel === 'SITE' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'COMPANY' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = siteOptions.filter(s => s.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : siteOptions;
    }
    return siteOptions;
  }, [siteOptions, viewLevel, selectedCompanyId, selectedGroupId]);

  const inputCls = "w-full px-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background";
  const labelCls = "text-xs font-medium mb-1 block text-foreground";
  const sectionTitleCls = "text-[11px] font-semibold uppercase tracking-wide text-foreground mb-2";
  const dateWrap = "relative";
  const dateInputCls = "w-full pl-8 pr-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background";
  const gridCls = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3";

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base">Filter Deliveries</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 pb-4">
        {/* Quick ranges */}
        <div>
          <div className={sectionTitleCls}>Quick Date Ranges</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_RANGES.map(({ key, label }) => {
              const dates = getQuickRangeDates(key);
              const isActive = filters.dateFrom === dates.dateFrom && filters.dateTo === dates.dateTo;
              return (
                <button
                  key={key}
                  onClick={() => {
                    onFilterChange('dateFrom', dates.dateFrom);
                    onFilterChange('dateTo', dates.dateTo);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Company & supplier */}
        <div>
          <div className={sectionTitleCls}>Company &amp; Supplier</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>Company Site</label>
              <SearchableSelect options={viewScopedSites} value={filters.site} onChange={(val) => onFilterChange('site', val)} placeholder="All Company Sites" />
            </div>
            <div>
              <label className={labelCls}>Supplier Group</label>
              <SearchableSelect options={supplierGroupOptions} value={filters.supplierGroup} onChange={(val) => onFilterChange('supplierGroup', val)} placeholder="All Supplier Groups" />
            </div>
            <div>
              <label className={labelCls}>Supplier Site</label>
              <SearchableSelect options={supplierOptions} value={filters.supplier} onChange={(val) => onFilterChange('supplier', val)} placeholder="All Supplier Sites" />
            </div>
            <div>
              <label className={labelCls}>Supplier Code</label>
              <input type="text" placeholder="Supplier code..." value={filters.supplierCode} onChange={(e) => onFilterChange('supplierCode', e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        {/* Order */}
        <div>
          <div className={sectionTitleCls}>Order</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>Part Number</label>
              <input type="text" placeholder="Part number..." value={textInputs.partNumber} onChange={(e) => onFilterChange('partNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>GRN Number</label>
              <input type="text" placeholder="GRN Number..." value={textInputs.grnNumber} onChange={(e) => onFilterChange('grnNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>PO Number</label>
              <input type="text" placeholder="PO Number..." value={textInputs.poNumber} onChange={(e) => onFilterChange('poNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Line Number</label>
              <input type="text" placeholder="Line number..." value={textInputs.lineNumber} onChange={(e) => onFilterChange('lineNumber', e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        {/* Commodity */}
        <div>
          <div className={sectionTitleCls}>Commodity</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>Commodity Group</label>
              <SearchableSelect options={commodityGroupOptions} value={filters.commodityGroup} onChange={(val) => onFilterChange('commodityGroup', val)} placeholder="All" />
            </div>
            <div>
              <label className={labelCls}>Commodity Type</label>
              <select value={filters.commodityType} onChange={(e) => onFilterChange('commodityType', e.target.value)} className={inputCls}>
                <option value="">All</option>
                {commodityTypeOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <div className={sectionTitleCls}>Status</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>PO Line Status</label>
              <select value={filters.poLineStatus} onChange={(e) => onFilterChange('poLineStatus', e.target.value)} className={inputCls}>
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>On-Time Status</label>
              <select value={filters.grnStatus} onChange={(e) => onFilterChange('grnStatus', e.target.value)} className={inputCls}>
                <option value="">All</option>
                <option value="on_time">On Time</option>
                <option value="early">Early</option>
                <option value="late">Late</option>
                <option value="early_late">Early/Late</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Appeal Status</label>
              <select value={filters.appealFilter} onChange={(e) => onFilterChange('appealFilter', e.target.value)} className={inputCls}>
                <option value="">All</option>
                <option value="no_appeal">Not Appealed</option>
                <option value="appealed">Appealed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div>
          <div className={sectionTitleCls}>Dates</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>Date From</label>
              <div className={dateWrap}>
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.dateFrom} onChange={(e) => onFilterChange('dateFrom', e.target.value)} className={dateInputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Date To</label>
              <div className={dateWrap}>
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.dateTo} onChange={(e) => onFilterChange('dateTo', e.target.value)} className={dateInputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>ODD From</label>
              <div className={dateWrap}>
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.oddFrom} onChange={(e) => onFilterChange('oddFrom', e.target.value)} className={dateInputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>ODD To</label>
              <div className={dateWrap}>
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.oddTo} onChange={(e) => onFilterChange('oddTo', e.target.value)} className={dateInputCls} />
              </div>
            </div>
          </div>
        </div>

        {/* Clear + Search buttons */}
        <div className="flex gap-2 pt-3 border-t">
          <Button size="sm" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button size="sm" variant="outline" onClick={onClearFilters}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button size="sm" variant="outline" onClick={onHideFilters}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Hide
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
